import { NonEditableCell, TextCell } from "@silevis/reactgrid";
import { format, parse } from "date-fns";
import { es } from "date-fns/locale";

import dates from "tools/dates";


const colorDay = {
  normal: { backgroundColor: "#F8FAFC", color: "#334155", fontWeight: "600" },
  saturday: { backgroundColor: "#CBD5E1", color: "#0F172A", fontWeight: "700" },
  sunday: { backgroundColor: "#94A3B8", color: "#FFFFFF", fontWeight: "700" },
};


export function getColor(id) {
  if (!id) return null;
  const h = (id * 137.508) % 360;
  return `hsl(${h}, 45%, 88%)`;
}

const SECTION_COL_WIDTH = 160;

const HOLIDAY_STRIPES =
 "repeating-linear-gradient(45deg, rgba(194, 198, 242, 0.5) 0 3px, transparent 3px 6px)";

function getDaysDifference(targetDateStr) {
  if (!targetDateStr) return null;
  const targetDate =
    typeof targetDateStr === "string"
      ? dates.getTrytonDate2Js(targetDateStr)
      : new Date(targetDateStr);
  if (!targetDate || isNaN(targetDate.getTime())) return null;

  const todayLocal = dates.startOfToday();
  const diffTime = todayLocal.getTime() - targetDate.getTime();
  return Math.round(diffTime / (1000 * 60 * 60 * 24));
}

export function isEventWithinLimit(shiftDateStr, eventShiftLimit) {
  if (!shiftDateStr) return true;
  const limit =
    eventShiftLimit !== null && eventShiftLimit !== undefined
      ? Number(eventShiftLimit)
      : 0;
  const diffDays = getDaysDifference(shiftDateStr);
  if (diffDays === null) return true;
  return diffDays <= limit;
}

export function isChangeShiftWithinLimit(shiftDateStr, changeShiftLimit) {
  if (!shiftDateStr) return true;
  const limit =
    changeShiftLimit !== null && changeShiftLimit !== undefined
      ? Number(changeShiftLimit)
      : 0;
  const diffDays = getDaysDifference(shiftDateStr);
  if (diffDays === null) return true;
  return diffDays <= limit;
}

export function getMondayOfWeek(dateInput) {
  if (!dateInput) return "";
  const d =
    typeof dateInput === "string"
      ? parse(dateInput, "yyyy-MM-dd", new Date())
      : new Date(dateInput);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  const monday = new Date(d.setDate(diff));
  return format(monday, "yyyy-MM-dd");
}

export function getWeekDates(mondayStr) {
  if (!mondayStr) return [];
  const datesArr = [];
  const monday = parse(mondayStr, "yyyy-MM-dd", new Date());
  for (let i = 0; i < 7; i++) {
    const current = new Date(monday);
    current.setDate(monday.getDate() + i);
    datesArr.push(format(current, "yyyy-MM-dd"));
  }
  return datesArr;
}

export function parseTimeToMinutes(timeVal) {
  if (!timeVal) return null;
  if (typeof timeVal === "object" && timeVal.hour !== undefined) {
    return timeVal.hour * 60 + (timeVal.minute || 0);
  }
  const str = String(timeVal).trim();
  const parts = str.split(":");
  if (parts.length >= 2) {
    const h = parseInt(parts[0], 10);
    const m = parseInt(parts[1], 10);
    if (!isNaN(h) && !isNaN(m)) return h * 60 + m;
  }
  return null;
}

export function isShiftMajorInNextDay(shiftKind) {
  if (!shiftKind?.start || !shiftKind?.end) return false;
  const startMins = parseTimeToMinutes(shiftKind.start);
  const endMins = parseTimeToMinutes(shiftKind.end);
  if (startMins === null || endMins === null) return false;
  if (endMins >= startMins) return false;
  const minsDay1 = 1440 - startMins;
  const minsDay2 = endMins;
  return minsDay2 > minsDay1;
}

export function getShiftHours(shiftKind, mealMap) {
  if (!shiftKind) return 0;
  const rawHours = Number(shiftKind.total_time || 0);
  const mealMins = mealMap?.get?.(shiftKind.id) || 0;
  const mealHours = mealMins / 60.0;
  return Math.max(0, Math.round((rawHours - mealHours) * 100) / 100);
}

export function getEffectiveShiftDate(shiftDateStr, shiftKind) {
  if (!shiftDateStr) return "";
  if (isShiftMajorInNextDay(shiftKind)) {
    const d = parse(shiftDateStr, "yyyy-MM-dd", new Date());
    d.setDate(d.getDate() + 1);
    return format(d, "yyyy-MM-dd");
  }
  return shiftDateStr;
}

function getEmployeeInitialRow(records, employeeId) {
  if (!records) return null;
  for (const sec of Object.values(records)) {
    const emp = sec.employees?.find(
      (e) => String(e.employee?.[0]) === String(employeeId)
    );
    if (emp) return emp;
  }
  return null;
}

function getEmployeeShiftKindsForDate(employeeId, date, cellsData, initialEmpRow) {
  if (cellsData) {
    const kinds = [];
    let matchedInGrid = false;
    for (const rec of cellsData.values()) {
      if (String(rec.employee) === String(employeeId) && rec.shift_date === date) {
        matchedInGrid = true;
        if (rec.shift_kind) kinds.push(rec.shift_kind);
      }
    }
    if (matchedInGrid) return kinds;
  }

  const raw = initialEmpRow?.[date];
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    const shiftId = parsed?.shift ? parseInt(parsed.shift, 10) : null;
    return shiftId ? [shiftId] : [];
  } catch {
    return [];
  }
}

export function calculateEmployeeWeekHours(
  employeeId,
  sundayDateStr,
  records,
  cellsData,
  shifts,
  mealMap
) {
  const mondayStr = getMondayOfWeek(sundayDateStr);
  const weekDates = getWeekDates(mondayStr);
  const weekSet = new Set(weekDates);
  const initialEmpRow = getEmployeeInitialRow(records, employeeId);

  const monday = parse(mondayStr, "yyyy-MM-dd", new Date());
  const prevDate = new Date(monday);
  prevDate.setDate(prevDate.getDate() - 1);
  const candidateDates = [format(prevDate, "yyyy-MM-dd"), ...weekDates];

  let total = 0;

  for (const date of candidateDates) {
    const shiftKindIds = getEmployeeShiftKindsForDate(
      employeeId,
      date,
      cellsData,
      initialEmpRow
    );

    for (const kindId of shiftKindIds) {
      const shiftKind = shifts?.get(kindId);
      if (!shiftKind) continue;
      if (shiftKind.mode && shiftKind.mode !== "work") continue;

      const effectiveDate = getEffectiveShiftDate(date, shiftKind);
      if (!weekSet.has(effectiveDate)) continue;

      total += getShiftHours(shiftKind, mealMap);
    }
  }

  return Math.round(total * 100) / 100;
}

export function refreshWeeklyTotals(
  cellsArray,
  records,
  cellsData,
  shifts,
  weekHoursWork,
  mealMap
) {
  return cellsArray.map((cell) => {
    if (
      cell.props?.isSunday &&
      cell.props?.employeeId &&
      cell.props?.shiftDate
    ) {
      const total = calculateEmployeeWeekHours(
        cell.props.employeeId,
        cell.props.shiftDate,
        records,
        cellsData,
        shifts,
        mealMap
      );
      return {
        ...cell,
        props: {
          ...cell.props,
          weeklyTotal: total,
          weekHoursLimit: weekHoursWork,
        },
      };
    }
    return cell;
  });
}

function getColumnsSchema(records) {
  const res = [
    { colIndex: 0, width: 350 },
    { colIndex: 1, width: SECTION_COL_WIDTH },
  ];
  let colNum = 2;
  for (const _ of records) {
    res.push({ colIndex: colNum, maxWidth: 90 });
    colNum += 1;
  }
  return res;
}

function getEmployeeValue(row) {
  return row.employee || Object.values(row)[0];
}

export function getEmployeeRows(records) {
  const res = [];
  for (const record of Object.values(records)) {
    const sectionId = record.id ?? null;
    const sectionName = sectionId ? record.value : "";
    for (const row of record.employees) {
      res.push({ row, sectionId, sectionName });
    }
  }
  return res.sort((a, b) => {
    const byName = String(getEmployeeValue(a.row)[1]).localeCompare(
      String(getEmployeeValue(b.row)[1]),
    );
    return byName || a.sectionName.localeCompare(b.sectionName);
  });
}

function getRowsSchema(employeeRows) {
  const rows = [
    { rowIndex: 0, height: 40 },
    { rowIndex: 1, height: 40 },
  ];
  for (let i = 0; i < employeeRows.length; i++) {
    rows.push({ rowIndex: i + 2, height: 46 });
  }
  return rows;
}

function setHeaderMonth(months, rowIdx, res) {
  let colIndex = 2;
  for (const month of months) {
    res.push({
      rowIndex: rowIdx,
      colIndex: colIndex,
      colSpan: month.colSpan,
      Template: NonEditableCell,
      props: { value: month.value, style: style.month },
    });
    colIndex += month.colSpan;
  }
}

function setHeaderRow(headers, rowIdx, res, holidays) {
  let colIdx = 0;
  res.push({
    rowIndex: rowIdx,
    colIndex: colIdx,
    Template: NonEditableCell,
    props: { value: "Empleado", style: style.header_section },
  });
  colIdx += 1;
  res.push({
    rowIndex: rowIdx,
    colIndex: colIdx,
    Template: NonEditableCell,
    props: { value: "Sección", style: style.header_day },
  });

  for (const dt of headers) {
    colIdx += 1;
    const value = dates.getTrytonDate2Js(dt);
    let dayOfWeek = "normal";
    if (value.getDay() === 6) {
      dayOfWeek = "saturday";
    } else if (value.getDay() === 0) {
      dayOfWeek = "sunday";
    }
    const fmtValue = format(value, "dd EEE", { locale: es });
    let headerStyle = { ...style.header_day, ...colorDay[dayOfWeek] };
    const isHoliday = holidays?.has(dt);
    if (isHoliday) {
      headerStyle = {
        ...style.header_holiday,
        backgroundImage: HOLIDAY_STRIPES,
      };
    }
    res.push({
      rowIndex: rowIdx,
      colIndex: colIdx,
      Template: NonEditableCell,
      props: { value: fmtValue, style: headerStyle },
    });
  }
}

const style = {
  header_section: {
    color: "#0369A1",
    backgroundColor: "#E0F2FE",
    display: "flex",
    justifyContent: "left",
    fontWeight: "bold",
    paddingLeft: "15px",
    fontFamily: "inherit",
    textOverflow: "ellipsis",
    fontSize: "0.9rem",
  },
  header_day: {
    color: "#334155",
    backgroundColor: "#F8FAFC",
    display: "flex",
    justifyContent: "center",
    fontSize: "0.85rem",
    fontWeight: "600",
  },
  header_holiday: {
    display: "flex",
    justifyContent: "center",
    fontSize: "0.85rem",
    fontWeight: "700",
  },
  month: {
    display: "flex",
    justifyContent: "left",
    paddingLeft: 15,
    color: "#475569",
    backgroundColor: "#FFFFFF",
    fontWeight: "bold",
    fontSize: "0.9rem",
  },
};

export {
  getColumnsSchema,
  getRowsSchema,
  setHeaderMonth,
  setHeaderRow,
};
